"use client";

import * as React from "react";

import {
  SidebarProvider,
  SidebarInset,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/animate-ui/components/radix/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
  DropdownMenuHighlight,
  DropdownMenuHighlightItem,
} from "@/components/animate-ui/primitives/radix/dropdown-menu";
import {
  ChevronsUpDown,
  CreditCard,
  GalleryVerticalEnd,
  Monitor,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  User,
  LogOut,
} from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  useCurrentUser,
  type CurrentUser,
} from "@/hooks/queries/useCurrentUser";
import { useProUpgradeLimitDialog } from "@/hooks/useProUpgradeLimitDialog";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { performClientLogout } from "@/lib/auth/client-session";
import { openNotebook } from "@/lib/notebook/navigation/open-notebook";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { isChatRoute } from "@/lib/platform/chat-route";
import {
  prefetchBillingRoute,
  prefetchHomeRoute,
  prefetchLibraryRoute,
  prefetchNotebookRoute,
  prefetchSessionsRoute,
  prefetchSettingsRoutes,
  prefetchTrashRoute,
} from "@/lib/client/navigation-prefetch";
import {
  useWorkspacesQuery,
  useCreateWorkspaceMutation,
  useDeleteWorkspaceMutation,
  useSwitchWorkspaceMutation,
  useUpdateWorkspaceMutation,
} from "@/hooks/queries/useWorkspacesQuery";
import { useScrollsInfiniteQuery } from "@/hooks/queries/useScrollsQuery";
import { useFoldersQuery } from "@/hooks/queries/useFoldersQuery";
import type { Workspace, Scroll } from "@/lib/supabase";
import { getRecentNotesByActivity, RECENT_NOTES_HOME_MAX } from "@/lib/home/recent-notes";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useSidebarPinStore } from "@/hooks/use-sidebar-pin";
import { useAppStore } from "@/stores/appStore";
import type { ActiveSmartView } from "@/stores/appStore";
import { useOSShortcut } from "@/hooks/use-os-shortcut";
import LottieIcon, {
  type LottieColorFilter,
} from "@/components/ui/lottie-icon";
import lottieDocument from "@/public/Animated Icons/icons8-document.json";
import lottieHome from "@/public/Animated Icons/icons8-home.json";
import lottiestar from "@/public/Animated Icons/icons8-star1.json";
import lottieSyncFolder from "@/public/Animated Icons/icons8-synchronize-folder.json";
import lottieTrash from "@/public/Animated Icons/icons8-trash.json";

// project nav removed
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SidebarExploreNav } from "@/components/sidebar/SidebarExploreNav";
import { FloatingFeedbackButton } from "@/components/feedback/FloatingFeedbackButton";
import { SignOutDialog } from "@/components/home/SignOutDialog";

const itemClassName =
  "relative z-[1] focus:text-accent-foreground select-none flex items-center gap-3 px-3 py-2.5 text-sm outline-none [&_svg]:size-4 [&_span]:data-[slot=dropdown-menu-shortcut]:text-xs [&_span]:data-[slot=dropdown-menu-shortcut]:ml-auto";

const separatorClassName = "-mx-1 my-2 h-px bg-border";

// Static nav structure — workspace-scoped navigation

const SIDEBAR_LOTTIE_SIZE = 18;
const SIDEBAR_LOTTIE_RECENT = 16;

/** Sidebar primary nav icon colors (Lottie + row tint). */
const SIDEBAR_ICON_HEX = {
  /** Home nav — crisp blue */
  home: "#2563EB",
  starred: "#E8A020",
  archive: "#7B68EE",
  trash: "#E05252",
  project: "#3BB87F",
  recent: "#6B7280",
} as const;

// project nav removed
const SIDEBAR_RECENT_LOTTIE_FILTERS: LottieColorFilter[] = [
  { keypath: "**", color: SIDEBAR_ICON_HEX.recent },
];

type WorkspaceNavItem = {
  title: string;
  url: string;
  lottie: object;
  view: ActiveSmartView;
  iconColor: string;
  colorFilters: LottieColorFilter[];
  loopOnHover?: boolean;
};

const NAV_WORKSPACE: WorkspaceNavItem[] = [
  {
    title: "Home",
    url: "/home",
    lottie: lottieHome,
    view: null,
    iconColor: SIDEBAR_ICON_HEX.home,
    colorFilters: [{ keypath: "**", color: SIDEBAR_ICON_HEX.home }],
    loopOnHover: false,
  },
  {
    title: "Starred",
    url: "/home",
    lottie: lottiestar,
    view: "starred",
    iconColor: SIDEBAR_ICON_HEX.starred,
    colorFilters: [{ keypath: "**", color: SIDEBAR_ICON_HEX.starred }],
    loopOnHover: false,
  },
  {
    title: "Archive",
    url: "/home",
    lottie: lottieSyncFolder,
    view: "archive",
    iconColor: SIDEBAR_ICON_HEX.archive,
    colorFilters: [{ keypath: "**", color: SIDEBAR_ICON_HEX.archive }],
    loopOnHover: false,
  },
  {
    title: "Trash",
    url: "/trash",
    lottie: lottieTrash,
    view: null,
    iconColor: SIDEBAR_ICON_HEX.trash,
    colorFilters: [{ keypath: "**", color: SIDEBAR_ICON_HEX.trash }],
    loopOnHover: false,
  },
];

// project nav removed

function WorkspaceNavRow({
  item,
  activeSmartView,
  setActiveSmartView,
  router,
  pathname,
  onHomeIntent,
  onTrashIntent,
}: {
  item: WorkspaceNavItem;
  activeSmartView: ActiveSmartView;
  setActiveSmartView: (view: ActiveSmartView) => void;
  router: ReturnType<typeof useRouter>;
  pathname: string;
  onHomeIntent?: () => void;
  onTrashIntent?: () => void;
}) {
  const [hover, setHover] = React.useState(false);
  const rowHover: Pick<
    React.HTMLAttributes<HTMLElement>,
    "onMouseEnter" | "onMouseLeave"
  > = {
    onMouseEnter: () => {
      setHover(true);
      if (item.url.startsWith("/home")) {
        onHomeIntent?.();
        router.prefetch("/home");
      }
      if (item.url === "/trash") {
        onTrashIntent?.();
        router.prefetch("/trash");
      }
    },
    onMouseLeave: () => setHover(false),
  };

  const lottie = (
    <LottieIcon
      animationData={item.lottie}
      size={SIDEBAR_LOTTIE_SIZE}
      iconColor={item.iconColor}
      colorFilters={item.colorFilters}
      speed={1.05}
      loopOnHover={item.loopOnHover ?? false}
      parentHovered={hover}
    />
  );

  const label = <span className="type-sidebar-item">{item.title}</span>;

  if (item.title === "Trash") {
    return (
      <SidebarMenuButton
        asChild
        tooltip={item.title}
        isActive={pathname === "/trash" || pathname.startsWith("/trash/")}
      >
        <Link href="/trash" {...rowHover}>
          {lottie}
          {label}
        </Link>
      </SidebarMenuButton>
    );
  }

  if (item.title === "Home") {
    return (
      <SidebarMenuButton
        asChild
        tooltip={item.title}
        isActive={activeSmartView === null}
      >
        <button
          type="button"
          {...rowHover}
          onClick={() => {
            setActiveSmartView(null);
            router.push("/home");
          }}
        >
          {lottie}
          {label}
        </button>
      </SidebarMenuButton>
    );
  }

  if (item.view !== null) {
    return (
      <SidebarMenuButton
        asChild
        tooltip={item.title}
        isActive={activeSmartView === item.view}
      >
        <button
          type="button"
          {...rowHover}
          onClick={() => {
            setActiveSmartView(item.view);
            router.push("/home");
          }}
        >
          {lottie}
          {label}
        </button>
      </SidebarMenuButton>
    );
  }

  return (
    <SidebarMenuButton asChild tooltip={item.title}>
      <a href={item.url} {...rowHover}>
        {lottie}
        {label}
      </a>
    </SidebarMenuButton>
  );
}

function SidebarRecentNoteRow({
  noteId,
  title,
  router,
  onNotebookIntent,
}: {
  noteId: string;
  title: string;
  router: ReturnType<typeof useRouter>;
  onNotebookIntent?: (notebookId: string) => void;
}) {
  const [hover, setHover] = React.useState(false);
  return (
    <SidebarMenuButton asChild tooltip={title || "Untitled"}>
      <a
        href={`/notebook/${noteId}`}
        onMouseEnter={() => {
          setHover(true);
          onNotebookIntent?.(noteId);
        }}
        onMouseLeave={() => setHover(false)}
      >
        <LottieIcon
          animationData={lottieDocument}
          size={SIDEBAR_LOTTIE_RECENT}
          iconColor={SIDEBAR_ICON_HEX.recent}
          colorFilters={SIDEBAR_RECENT_LOTTIE_FILTERS}
          className="shrink-0"
          speed={1.05}
          parentHovered={hover}
        />
        <span className="type-sidebar-item truncate">
          {title || "Untitled"}
        </span>
      </a>
    </SidebarMenuButton>
  );
}

// ---------------------------------------------------------------------------
// Hover sidebar state machine — production-hardened
// States:
//   collapsed   → default, sidebar shows icon-only rail
//   hover-open  → mouse is over sidebar or an open dropdown; auto-collapses on leave
//   locked-open → user double-clicked; stays open until double-click again
//
// Design principles:
//   • ALL mutable state that drives timer/event logic lives in refs to avoid
//     stale-closure render loops — mode state is only used for React rendering.
//   • A single stable clearLeave ref-fn avoids recreating callbacks.
//   • dropdownCount tracks MULTIPLE open dropdowns (not just a boolean) so
//     nested menus don't prematurely unblock the close timer.
//   • ESC key closes dropdowns and/or collapses sidebar (proper priority).
//   • Mouse pointer bridge: 300ms grace window handles cursor dead-zones.
//   • All timers are cleaned up on unmount.
//   • isMobile guard eliminates all hover logic on touch devices.
// ---------------------------------------------------------------------------
type SidebarMode = "collapsed" | "hover-open" | "locked-open";

function useSidebarHover(isMobile: boolean) {
  const isPinned = useSidebarPinStore((s) => s.isPinned);
  const [mode, setMode] = React.useState<SidebarMode>("collapsed");

  // Use a ref mirror of mode so timer callbacks always read current value
  // without needing to be recreated on every mode change.
  const modeRef = React.useRef<SidebarMode>("collapsed");
  const syncMode = (next: SidebarMode) => {
    if (modeRef.current === next) return; // skip unnecessary state updates
    modeRef.current = next;
    setMode(next);
  };

  const leaveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownCount = React.useRef(0); // count of currently open dropdowns
  const isInsideSidebar = React.useRef(false); // tracks pointer position

  const isOpen = mode !== "collapsed";

  // Stable clearLeave — never changes reference
  const clearLeave = React.useCallback(() => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
  }, []);

  // Stable scheduleClose — reads modeRef/dropdownCount so no stale closure
  const scheduleClose = React.useCallback(() => {
    if (modeRef.current === "locked-open") return;
    clearLeave();
    leaveTimer.current = setTimeout(() => {
      leaveTimer.current = null;
      // Only collapse if: not locked, no dropdown open, pointer not inside sidebar
      if (
        modeRef.current !== "locked-open" &&
        dropdownCount.current === 0 &&
        !isInsideSidebar.current
      ) {
        syncMode("collapsed");
      }
    }, 300);
  }, [clearLeave]);

  React.useEffect(() => {
    if (isMobile) return;
    clearLeave();
    if (isPinned) syncMode("locked-open");
    else if (modeRef.current === "locked-open") syncMode("collapsed");
  }, [isPinned, isMobile, clearLeave]);

  React.useEffect(() => {
    if (isMobile) return;
    const onPinChange = (e: Event) => {
      const pinned = Boolean(
        (e as CustomEvent<{ pinned?: boolean }>).detail?.pinned,
      );
      clearLeave();
      syncMode(pinned ? "locked-open" : "collapsed");
    };
    window.addEventListener("inora-sidebar-pin-change", onPinChange);
    return () =>
      window.removeEventListener("inora-sidebar-pin-change", onPinChange);
  }, [isMobile, clearLeave]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
    };
  }, []);

  // ESC key: collapse hover-open or close any dropdown (dropdowns handle their
  // own ESC via Radix, so this only handles sidebar-level collapse)
  React.useEffect(() => {
    if (isMobile) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (modeRef.current === "hover-open" && dropdownCount.current === 0) {
        syncMode("collapsed");
      }
    };
    window.addEventListener("keydown", handler, { passive: true });
    return () => window.removeEventListener("keydown", handler);
  }, [isMobile]);

  const handleSidebarEnter = React.useCallback(() => {
    if (isMobile) return;
    isInsideSidebar.current = true;
    clearLeave();
    if (modeRef.current === "collapsed") syncMode("hover-open");
  }, [isMobile, clearLeave]);

  const handleSidebarLeave = React.useCallback(() => {
    if (isMobile) return;
    isInsideSidebar.current = false;
    if (modeRef.current === "locked-open") return;
    scheduleClose();
  }, [isMobile, scheduleClose]);

  const handleEdgeEnter = React.useCallback(() => {
    if (isMobile) return;
    isInsideSidebar.current = true;
    clearLeave();
    if (modeRef.current === "collapsed") syncMode("hover-open");
  }, [isMobile, clearLeave]);

  // Called by every dropdown's onOpenChange — tracks count, not just boolean
  const notifyDropdown = React.useCallback(
    (open: boolean) => {
      if (open) {
        dropdownCount.current += 1;
        clearLeave(); // cancel any pending close while a dropdown is open
      } else {
        dropdownCount.current = Math.max(0, dropdownCount.current - 1);
        if (dropdownCount.current === 0 && modeRef.current === "hover-open") {
          // All dropdowns closed — schedule collapse if pointer left sidebar
          scheduleClose();
        }
      }
    },
    [clearLeave, scheduleClose],
  );

  // Force-close all dropdowns + collapse sidebar (called when sidebar collapses externally)
  const collapseAll = React.useCallback(() => {
    dropdownCount.current = 0;
    clearLeave();
    syncMode("collapsed");
  }, [clearLeave]);

  return {
    isOpen,
    mode,
    handleSidebarEnter,
    handleSidebarLeave,
    handleEdgeEnter,
    notifyDropdown,
    collapseAll,
  };
}

interface RadixSidebarDemoProps {
  children?: React.ReactNode;
  onNavigate?: (id: string) => void;
  /** Server bootstrap — keeps SSR and first client paint in sync for the profile menu. */
  initialCurrentUser?: CurrentUser | null;
}

export const RadixSidebarDemo = ({
  children,
  onNavigate,
  initialCurrentUser,
}: RadixSidebarDemoProps) => {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const shortcutKey = useOSShortcut();
  const router = useRouter();
  const pathname = usePathname();
  const { data: currentUser } = useCurrentUser({
    initialData: initialCurrentUser ?? undefined,
  });
  const {
    showFromErrorCode,
    handlePlanAccessError,
    guardWriteAccess,
    proLimitDialog,
  } = useProUpgradeLimitDialog();
  const { data: workspacesData } = useWorkspacesQuery();
  const createWorkspace = useCreateWorkspaceMutation();
  const deleteWorkspace = useDeleteWorkspaceMutation();
  const switchWorkspace = useSwitchWorkspaceMutation();
  const updateWorkspace = useUpdateWorkspaceMutation();

  // Inline rename state
  const [renamingId, setRenamingId] = React.useState<string | null>(null);
  const [renameValue, setRenameValue] = React.useState("");
  const renameInputRef = React.useRef<HTMLInputElement>(null);

  // New workspace dialog state
  const [showNewWsDialog, setShowNewWsDialog] = React.useState(false);
  const [newWsValue, setNewWsValue] = React.useState("");
  const newWsInputRef = React.useRef<HTMLInputElement>(null);

  // Logout AlertDialog state
  const [showLogoutDialog, setShowLogoutDialog] = React.useState(false);

  // Delete workspace AlertDialog state
  const [wsToDelete, setWsToDelete] = React.useState<Workspace | null>(null);

  const workspaces = workspacesData?.workspaces ?? [];
  const activeWorkspaceId = workspacesData?.active_workspace_id ?? null;
  const activeWorkspace: Workspace | undefined = React.useMemo(
    () => workspaces.find((w) => w.id === activeWorkspaceId) ?? workspaces[0],
    [workspaces, activeWorkspaceId],
  );
  const workspaceScopeId = activeWorkspace?.id ?? activeWorkspaceId ?? null;

  // Recent notes: activity order (updated_at) via shared helper — same cap as home section
  // We pull directly from the React Query cache so no extra fetch is needed
  const { data: scrollsData } = useScrollsInfiniteQuery({
    limit: 100,
    workspaceId: workspaceScopeId,
  });
  const { data: sidebarFolders = [] } = useFoldersQuery(workspaceScopeId);
  const allForRecent = React.useMemo((): Scroll[] => {
    const all: Scroll[] = scrollsData?.pages.flatMap((p) => p.items) ?? [];
    return getRecentNotesByActivity(all, 50_000, sidebarFolders);
  }, [scrollsData, sidebarFolders]);
  const recentNotes = allForRecent.slice(0, RECENT_NOTES_HOME_MAX);
  const showRecentLibraryMore =
    allForRecent.length > RECENT_NOTES_HOME_MAX;

  const hydrateSidebarPin = useSidebarPinStore((s) => s.hydrate);

  React.useEffect(() => {
    hydrateSidebarPin();
  }, [hydrateSidebarPin]);

  const {
    isOpen,
    handleSidebarEnter,
    handleSidebarLeave,
    handleEdgeEnter,
    notifyDropdown,
    collapseAll,
  } = useSidebarHover(!!isMobile);

  const setActiveSmartView = useAppStore((s) => s.setActiveSmartView);
  const activeSmartView = useAppStore((s) => s.activeSmartView);
  const onChatRoute = isChatRoute(pathname);

  const handleLogout = React.useCallback(async () => {
    collapseAll();
    setShowLogoutDialog(false);
    await performClientLogout(queryClient);
    router.push("/");
    router.refresh();
  }, [collapseAll, queryClient, router]);

  // New workspace dialog
  const handleCreateWorkspace = React.useCallback(() => {
    setNewWsValue("");
    setShowNewWsDialog(true);
  }, []);

  const commitNewWorkspace = React.useCallback(() => {
    const name = newWsValue.trim();
    setShowNewWsDialog(false);
    setNewWsValue("");
    if (!name) return;
    const tid = toast.loading("Creating workspace…");
    createWorkspace.mutate(name, {
      onSuccess: () =>
        toast.success(`Workspace "${name}" created`, { id: tid }),
      onError: (e: Error & { code?: string }) => {
        if (showFromErrorCode(e.code)) {
          toast.dismiss(tid);
          return;
        }
        toast.error(e.message ?? "Failed to create workspace", { id: tid });
      },
    });
  }, [newWsValue, createWorkspace, showFromErrorCode]);

  const handleSwitchWorkspace = React.useCallback(
    (id: string) => {
      if (id === activeWorkspaceId) return;
      switchWorkspace.mutate(id, {
        onError: () => toast.error("Failed to switch workspace"),
      });
    },
    [activeWorkspaceId, switchWorkspace],
  );

  const handleDeleteWorkspace = React.useCallback(
    (ws: Workspace, e: React.MouseEvent) => {
      e.stopPropagation();
      setWsToDelete(ws);
    },
    [],
  );

  const confirmDeleteWorkspace = React.useCallback(() => {
    if (!wsToDelete) return;
    const name = wsToDelete.name;
    const tid = toast.loading(`Deleting "${name}"…`);
    deleteWorkspace.mutate(wsToDelete.id, {
      onSuccess: () => toast.success(`"${name}" deleted`, { id: tid }),
      onError: (e: Error) => {
        if (handlePlanAccessError(e)) {
          toast.dismiss(tid);
          return;
        }
        toast.error(e.message ?? "Failed to delete workspace", { id: tid });
      },
    });
    setWsToDelete(null);
  }, [wsToDelete, deleteWorkspace, handlePlanAccessError]);

  // Inline rename
  const startRename = React.useCallback(
    (ws: Workspace, e: React.MouseEvent) => {
      e.stopPropagation();
      setRenamingId(ws.id);
      setRenameValue(ws.name);
      setTimeout(() => {
        renameInputRef.current?.focus();
        renameInputRef.current?.select();
      }, 50);
    },
    [],
  );

  const commitRename = React.useCallback(() => {
    const name = renameValue.trim();
    const id = renamingId;
    setRenamingId(null);
    setRenameValue("");
    if (!name || !id) return;
    const ws = workspaces.find((w) => w.id === id);
    if (!ws || name === ws.name) return;
    if (!guardWriteAccess({ workspace: ws })) return;
    updateWorkspace.mutate(
      { id, name },
      {
        onSuccess: () => toast.success(`Renamed to "${name}"`),
        onError: (e: Error & { code?: string }) => {
          if (handlePlanAccessError(e)) return;
          toast.error("Failed to rename workspace");
        },
      },
    );
  }, [renamingId, renameValue, workspaces, updateWorkspace, guardWriteAccess, handlePlanAccessError]);

  // ⌘1-9 keyboard shortcut: switch to nth workspace
  React.useEffect(() => {
    if (isMobile) return;
    const handler = (e: KeyboardEvent) => {
      if (!e.metaKey && !e.ctrlKey) return;
      const n = parseInt(e.key, 10);
      if (isNaN(n) || n < 1 || n > 9) return;
      const target = workspaces[n - 1];
      if (!target) return;
      e.preventDefault();
      handleSwitchWorkspace(target.id);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isMobile, workspaces, handleSwitchWorkspace]);

  return (
    <SidebarProvider
      open={isMobile ? undefined : isOpen}
      onOpenChange={undefined}
    >
      {/* Invisible left-edge sensor — 16 px wide, full height */}
      {!isMobile && (
        <div
          className="fixed left-0 top-0 z-50 h-full w-4 select-none pointer-events-auto"
          onMouseEnter={handleEdgeEnter}
        />
      )}

      <Sidebar
        variant="floating"
        collapsible="icon"
        onMouseEnter={handleSidebarEnter}
        onMouseLeave={handleSidebarLeave}
      >
        <SidebarHeader>
          {/* Workspace Switcher */}
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu
                onOpenChange={(open) => {
                  notifyDropdown(open);
                  if (!open) {
                    setRenamingId(null);
                    setRenameValue("");
                    setNewWsValue("");
                  }
                }}
              >
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    className="gap-3 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  >
                    <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                      <GalleryVerticalEnd className="size-4" />
                    </div>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {activeWorkspace?.name ?? "My Workspace"}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">
                        {workspaces.length > 1
                          ? `${workspaces.length} workspaces`
                          : "Workspace"}
                      </span>
                    </div>
                    <ChevronsUpDown className="ml-auto shrink-0" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-56 overflow-hidden rounded-xl border bg-background p-1 z-50"
                  align="start"
                  side={isMobile ? "bottom" : "right"}
                  sideOffset={4}
                  onPointerEnter={() => notifyDropdown(true)}
                  onPointerLeave={() => notifyDropdown(false)}
                >
                  <DropdownMenuHighlight className="absolute inset-0 bg-neutral-100 dark:bg-white/10 ring-1 ring-black/5 dark:ring-white/10 z-0 rounded-md shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                    {/* Active workspace header */}
                    <DropdownMenuLabel className="p-0 font-normal">
                      <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                        <div className="flex size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                          <GalleryVerticalEnd className="size-4" />
                        </div>
                        <div className="grid flex-1 text-left text-sm leading-tight">
                          <span className="truncate font-semibold">
                            {activeWorkspace?.name ?? "My Workspace"}
                          </span>
                          <span className="truncate text-xs">
                            {activeWorkspace?.is_default
                              ? "Default"
                              : "Workspace"}
                          </span>
                        </div>
                      </div>
                    </DropdownMenuLabel>

                    <DropdownMenuSeparator className="-mx-1 my-1 h-px bg-border" />

                    <DropdownMenuGroup>
                      {workspaces.map((ws, index) => (
                        <DropdownMenuHighlightItem key={ws.id}>
                          {renamingId === ws.id ? (
                            /* Inline rename input */
                            <div className="flex items-center gap-1.5 px-1.5 py-1">
                              <div className="flex size-6 items-center justify-center rounded-sm border bg-background shrink-0">
                                <GalleryVerticalEnd className="size-3.5 shrink-0" />
                              </div>
                              <input
                                ref={renameInputRef}
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onBlur={commitRename}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    commitRename();
                                  }
                                  if (e.key === "Escape") {
                                    setRenamingId(null);
                                    setRenameValue("");
                                  }
                                }}
                                className="flex-1 min-w-0 bg-transparent text-sm outline-none border-b border-primary/60 pb-px caret-primary"
                                maxLength={80}
                              />
                            </div>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => handleSwitchWorkspace(ws.id)}
                              className="relative z-[1] focus:text-accent-foreground select-none flex items-center gap-2 px-2 py-1.5 text-sm outline-none [&_svg]:size-4 [&_span]:data-[slot=dropdown-menu-shortcut]:text-xs [&_span]:data-[slot=dropdown-menu-shortcut]:ml-auto"
                            >
                              <div className="flex size-6 items-center justify-center rounded-sm border bg-background shrink-0">
                                <GalleryVerticalEnd className="size-3.5 shrink-0" />
                              </div>
                              <span className="truncate flex-1">{ws.name}</span>
                              <button
                                onClick={(e) => startRename(ws, e)}
                                className="ml-1 shrink-0 rounded p-0.5 opacity-0 group-hover/highlight:opacity-100 hover:text-foreground text-muted-foreground transition-opacity"
                                tabIndex={-1}
                                aria-label={`Rename ${ws.name}`}
                              >
                                <Pencil className="size-3" />
                              </button>
                              <DropdownMenuShortcut>
                                {shortcutKey}{index + 1}
                              </DropdownMenuShortcut>
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuHighlightItem>
                      ))}

                      {workspaces.length === 0 && (
                        <div className="px-2 py-3 text-xs text-muted-foreground text-center">
                          No workspaces yet
                        </div>
                      )}

                      <DropdownMenuSeparator className="-mx-1 my-1 h-px bg-border" />

                      {/* New workspace trigger — opens Dialog outside dropdown */}
                      <DropdownMenuHighlightItem>
                        <DropdownMenuItem
                          className="relative z-[1] focus:text-accent-foreground select-none flex items-center gap-2 px-2 py-1.5 text-sm outline-none [&_svg]:size-4 [&_span]:data-[slot=dropdown-menu-shortcut]:text-xs [&_span]:data-[slot=dropdown-menu-shortcut]:ml-auto"
                          onClick={handleCreateWorkspace}
                        >
                          <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                            <Plus className="size-4" />
                          </div>
                          <span>New Workspace</span>
                        </DropdownMenuItem>
                      </DropdownMenuHighlightItem>

                    </DropdownMenuGroup>
                  </DropdownMenuHighlight>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        <SidebarContent className="gap-0 py-1">
          {/* Workspace Nav */}
          <SidebarGroup className="px-2 pt-3 pb-1">
            <SidebarGroupLabel className="mb-1 px-1 text-[10.5px] font-semibold uppercase tracking-widest text-muted-foreground/50">Navigate</SidebarGroupLabel>
            <SidebarMenu className="gap-0.5">
              {NAV_WORKSPACE.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <WorkspaceNavRow
                    item={item}
                    activeSmartView={activeSmartView}
                    setActiveSmartView={setActiveSmartView}
                    router={router}
                    pathname={pathname}
                    onHomeIntent={() =>
                      prefetchHomeRoute(queryClient, activeWorkspaceId)
                    }
                    onTrashIntent={() =>
                      prefetchTrashRoute(queryClient, activeWorkspaceId)
                    }
                  />
                </SidebarMenuItem>
              ))}
              <SidebarExploreNav />
            </SidebarMenu>
          </SidebarGroup>
          {/* Workspace Nav */}

          {/* Divider */}
          <div className="mx-3 my-1 h-px bg-sidebar-border/50" />
          {/* Divider */}

          {/* Folder Tree */}
          {/* Folders are rendered in the main panel (not sidebar) */}
          {/* Folder Tree */}

          {/* Recent Notes */}
          <SidebarGroup className="group-data-[collapsible=icon]:hidden px-2 pt-2 pb-2">
            <SidebarGroupLabel className="mb-1 px-1 text-[10.5px] font-semibold uppercase tracking-widest text-muted-foreground/50">Recent Notes</SidebarGroupLabel>
            <SidebarMenu className="gap-0.5">
              {recentNotes.length === 0 ? (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    disabled
                    className="text-sidebar-foreground/40 cursor-default"
                  >
                    <span className="text-xs">No notes yet</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ) : (
                recentNotes.map((note) => (
                  <SidebarMenuItem key={note.id}>
                    <SidebarRecentNoteRow
                      noteId={note.id}
                      title={note.title ?? ""}
                      router={router}
                      onNotebookIntent={(id) =>
                        prefetchNotebookRoute(router, id)
                      }
                    />
                  </SidebarMenuItem>
                ))
              )}
              {showRecentLibraryMore && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    className="text-sidebar-foreground/70"
                  >
                    <Link
                      href="/library"
                      onMouseEnter={() => {
                        router.prefetch("/library");
                        prefetchLibraryRoute(queryClient, activeWorkspaceId);
                      }}
                    >
                      <MoreHorizontal className="shrink-0 text-[#6B7280]" />
                      <span className="type-sidebar-item">More</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroup>
          {/* Recent Notes */}
        </SidebarContent>

        <SidebarFooter>
          {/* Nav User */}
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu
                onOpenChange={(open) => {
                  notifyDropdown(open);
                }}
              >
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    className="group data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  >
                    <UserAvatar
                      src={currentUser?.avatar_url}
                      name={currentUser?.username}
                      size="md"
                      shape="circle"
                      className="ring-1 ring-border/70 dark:ring-white/10"
                    />
                    <div className="grid min-w-0 flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {currentUser?.username ?? "…"}
                      </span>
                      <span className="truncate text-[11px] text-muted-foreground group-data-[collapsible=icon]:hidden">
                        {shortcutKey}K search · {shortcutKey}1–9 workspaces
                      </span>
                    </div>
                    <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-60" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className={cn(
                    "relative min-w-[15rem] w-[17.5rem] overflow-hidden rounded-xl border border-border/60 bg-popover/95 p-2.5 shadow-xl backdrop-blur-md z-50",
                  )}
                  side={isMobile ? "bottom" : "right"}
                  align="end"
                  sideOffset={4}
                  onPointerEnter={() => notifyDropdown(true)}
                  onPointerLeave={() => notifyDropdown(false)}
                >
                  <DropdownMenuHighlight className="rounded-lg bg-neutral-100 dark:bg-white/10 ring-1 ring-black/5 dark:ring-white/10 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                    <DropdownMenuLabel className="p-0 font-normal">
                      <div className="flex items-center gap-3 px-2 py-3 text-left text-sm">
                        <UserAvatar
                          src={currentUser?.avatar_url}
                          name={currentUser?.username}
                          size="md"
                          priority
                        />
                        <div className="grid min-w-0 flex-1 text-left text-sm leading-tight">
                          <span className="truncate font-semibold">
                            {currentUser?.username ?? "…"}
                          </span>
                          <span className="truncate text-[11px] text-muted-foreground">
                            Profile & account
                          </span>
                        </div>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className={separatorClassName} />

                    <DropdownMenuHighlightItem>
                      <DropdownMenuItem className={itemClassName} asChild>
                        <Link
                          href="/settings/account"
                          prefetch
                          onMouseEnter={() => prefetchSettingsRoutes(queryClient)}
                          className="flex w-full items-center gap-3"
                        >
                          <User className="size-4 shrink-0 text-muted-foreground" />
                          Account
                        </Link>
                      </DropdownMenuItem>
                    </DropdownMenuHighlightItem>
                    <DropdownMenuHighlightItem>
                      <DropdownMenuItem className={itemClassName} asChild>
                        <Link
                          href="/settings/sessions"
                          prefetch
                          onMouseEnter={() => prefetchSessionsRoute(queryClient)}
                          className="flex w-full items-center gap-3"
                        >
                          <Monitor className="size-4 shrink-0 text-muted-foreground" />
                          Sessions
                        </Link>
                      </DropdownMenuItem>
                    </DropdownMenuHighlightItem>
                    <DropdownMenuHighlightItem>
                      <DropdownMenuItem className={itemClassName} asChild>
                        <Link
                          href="/billing"
                          prefetch
                          onMouseEnter={() => prefetchBillingRoute(queryClient)}
                          className="flex w-full items-center gap-3"
                        >
                          <CreditCard className="size-4 shrink-0 text-muted-foreground" />
                          Billing
                        </Link>
                      </DropdownMenuItem>
                    </DropdownMenuHighlightItem>

                    <DropdownMenuSeparator className={separatorClassName} />

                    <DropdownMenuHighlightItem>
                      <DropdownMenuItem
                        className={cn(
                          itemClassName,
                          "text-[#ef4444]/80 focus:text-[#ef4444]/80",
                        )}
                        onSelect={(e) => {
                          e.preventDefault();
                          setShowLogoutDialog(true);
                        }}
                      >
                        <span className="flex w-full items-center gap-3">
                          <LogOut className="size-4 shrink-0 opacity-80" />
                          Log out
                        </span>
                      </DropdownMenuItem>
                    </DropdownMenuHighlightItem>
                  </DropdownMenuHighlight>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
          {/* Nav User */}
        </SidebarFooter>
      </Sidebar>

      <SidebarInset
        className={cn(
          "flex min-h-0 min-w-0 w-full flex-1 overflow-hidden rounded-xl",
          onChatRoute
            ? "h-[calc(100svh-1rem)] max-h-[calc(100svh-1rem)] min-h-0 overflow-hidden"
            : "",
        )}
      >
        {children}
      </SidebarInset>

      <FloatingFeedbackButton />

      {/* ── New Workspace Dialog ── */}
      <Dialog
        open={showNewWsDialog}
        onOpenChange={(o) => {
          setShowNewWsDialog(o);
          if (!o) setNewWsValue("");
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>New Workspace</DialogTitle>
            <DialogDescription>
              Give your workspace a name. You can rename it anytime.
            </DialogDescription>
          </DialogHeader>
          <input
            ref={newWsInputRef}
            autoFocus
            value={newWsValue}
            onChange={(e) => setNewWsValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitNewWorkspace();
              }
              if (e.key === "Escape") {
                setShowNewWsDialog(false);
                setNewWsValue("");
              }
            }}
            placeholder="e.g. Personal, Work, Research…"
            maxLength={80}
            className="w-full rounded-lg border border-input bg-transparent px-3 py-2.5 text-sm outline-none placeholder:text-muted-foreground/50 focus:ring-1 focus:ring-ring transition-shadow"
          />
          <DialogFooter className="gap-2">
            <button
              type="button"
              onClick={() => {
                setShowNewWsDialog(false);
                setNewWsValue("");
              }}
              className="flex-1 rounded-lg border border-input px-4 py-2 text-sm hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!newWsValue.trim()}
              onClick={commitNewWorkspace}
              className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Create
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Workspace AlertDialog ── */}
      <AlertDialog
        open={!!wsToDelete}
        onOpenChange={(o) => {
          if (!o) setWsToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete &ldquo;{wsToDelete?.name}&rdquo;?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the workspace and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setWsToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteWorkspace}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SignOutDialog
        open={showLogoutDialog}
        onOpenChange={setShowLogoutDialog}
        onConfirm={() => void handleLogout()}
      />

      {proLimitDialog}

    </SidebarProvider>
  );
};

// ─── end of file ───
